import { useTranslation } from 'react-i18next';
import Card from '@/components/base/Card';
import { cn } from '@/lib/cn';

const OPTIONS = [
  { code: 'ar' as const, labelKey: 'settings.lang.ar', descKey: 'settings.lang.arDesc', icon: 'ri-translate-2' },
  { code: 'en' as const, labelKey: 'settings.lang.en', descKey: 'settings.lang.enDesc', icon: 'ri-english-input' },
];

/** Dashboard language + interface direction. Keeps the current page & inputs. */
export default function LanguageSection() {
  const { t, i18n } = useTranslation();
  const active = i18n.language.startsWith('ar') ? 'ar' : 'en';

  const change = (lang: 'ar' | 'en') => {
    if (lang === active) return;
    i18n.changeLanguage(lang);
    try {
      window.localStorage.setItem('lb_lang', lang);
    } catch {
      // ignore storage errors
    }
  };

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-start gap-3 border-b border-background-200 px-4 py-4 md:px-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className="ri-translate-2 text-xl leading-none" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-foreground-950">{t('settings.section.language')}</h2>
          <p className="mt-0.5 text-xs text-foreground-500">{t('settings.section.languageDesc')}</p>
        </div>
      </div>

      <div className="p-4 md:p-5">
        <p className="mb-3 text-xs text-foreground-500">
          {t('settings.lang.current')}:{' '}
          <span className="font-semibold text-foreground-800">
            {active === 'ar' ? t('settings.lang.ar') : t('settings.lang.en')}
          </span>
        </p>

        <div role="radiogroup" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {OPTIONS.map((option) => {
            const isActive = option.code === active;
            return (
              <button
                key={option.code}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => change(option.code)}
                className={cn(
                  'flex items-start gap-3 rounded-lg border px-4 py-3.5 text-start transition-colors cursor-pointer',
                  isActive
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-background-300 bg-background-50 hover:bg-background-100',
                )}
              >
                <span
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
                    isActive ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500',
                  )}
                >
                  <i className={cn(option.icon, 'text-xl leading-none')} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-950">{t(option.labelKey)}</span>
                    {isActive ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800">
                        <i className="ri-check-line text-sm" aria-hidden="true" />
                        {t('settings.lang.selected')}
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-0.5 block text-xs text-foreground-500">{t(option.descKey)}</span>
                </span>
              </button>
            );
          })}
        </div>

        <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
          {t('settings.lang.note')}
        </p>
      </div>
    </Card>
  );
}